package com.mxpainting.components.sections

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import com.mxpainting.Strings
import com.mxpainting.rememberLanguage
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.classNames
import com.varabyte.kobweb.compose.ui.modifiers.id
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.H4
import org.jetbrains.compose.web.dom.H6
import org.jetbrains.compose.web.dom.I
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text
import com.mxpainting.Language

data class Service(val title: String, val description: String, val imgSrc: String)

fun getServices(language: Language) = listOf(
    Service(Strings.servicesCarouselIntPaintTitle[language].orEmpty(), Strings.servicesCarouselIntPaintDesc[language].orEmpty(), "/img/interior-painting.jpg"),
    Service(Strings.servicesCarouselExtPaintTitle[language].orEmpty(), Strings.servicesCarouselExtPaintDesc[language].orEmpty(), "/img/exterior-painting.jpg"),
    Service(Strings.servicesCarouselPowerWashTitle[language].orEmpty(), Strings.servicesCarouselPowerWashDesc[language].orEmpty(), "/img/power-washing.jpg"),
    Service(Strings.servicesCarouselRepairsTitle[language].orEmpty(), Strings.servicesCarouselRepairsDesc[language].orEmpty(), "/img/repairs.jpg")
)

@Composable
fun ServicesCarousel() {
    val language by rememberLanguage()
    val services = getServices(language)

    Div(Modifier.classNames("container-xxl", "py-5").toAttrs()) {
        Div(Modifier.classNames("container").toAttrs()) {
            Div(Modifier.classNames("text-center", "mx-auto", "mb-5").styleModifier { property("max-width", "600px") }.toAttrs()) {
                H6(Modifier.classNames("section-title", "bg-white", "text-center", "text-primary", "px-3").toAttrs()) { 
                    Text(Strings.servicesCarouselHeader[language].orEmpty())
                }
                H1(Modifier.classNames("display-5", "mb-4").toAttrs()) { 
                    Text(Strings.servicesCarouselSubHeader[language].orEmpty())
                }
            }
            
            Div(Modifier.classNames("carousel", "slide").id("services-carousel").toAttrs {
                attr("data-bs-ride", "carousel")
            }) {
                Div(Modifier.classNames("carousel-inner", "pb-4").toAttrs()) {
                    services.indices.forEach { index ->
                        val itemClasses = mutableListOf("carousel-item")
                        if (index == 0) itemClasses.add("active")
                        
                        Div(Modifier.classNames(*itemClasses.toTypedArray()).toAttrs()) {
                            Div(Modifier.classNames("row", "g-4").toAttrs()) {
                                // For desktop, we show 3 items. For mobile, only the current one.
                                // This implementation uses Bootstrap grid to show/hide items based on screen size.
                                ServiceItem(services[index], language, "col-lg-4", "col-md-6")
                                ServiceItem(services[(index + 1) % services.size], language, "col-lg-4", "col-md-6", "d-none", "d-md-block")
                                ServiceItem(services[(index + 2) % services.size], language, "col-lg-4", "d-none", "d-lg-block")
                            }
                        }
                    }
                }
                
                // Controls - positioned differently for services carousel
                Div(Modifier.classNames("d-flex", "justify-content-center", "gap-2", "mt-4").toAttrs()) {
                    Button(attrs = Modifier.classNames("btn", "btn-primary", "btn-square").toAttrs {
                        attr("data-bs-target", "#services-carousel")
                        attr("data-bs-slide", "prev")
                    }) {
                        I(Modifier.classNames("fa", "fa-chevron-left").toAttrs()) {}
                    }
                    Button(attrs = Modifier.classNames("btn", "btn-primary", "btn-square").toAttrs {
                        attr("data-bs-target", "#services-carousel")
                        attr("data-bs-slide", "next")
                    }) {
                        I(Modifier.classNames("fa", "fa-chevron-right").toAttrs()) {}
                    }
                }
            }
        }
    }
}

@Composable
private fun ServiceItem(service: Service,  language: Language, vararg columnClasses: String) {
    Div(Modifier.classNames(*columnClasses).toAttrs()) {
        Div(Modifier.classNames("service-item", "rounded", "overflow-hidden").toAttrs()) {
            Img(src = service.imgSrc, alt = service.title, attrs = Modifier.classNames("img-fluid").toAttrs())
            Div(Modifier.classNames("p-4").toAttrs()) {
                H4(Modifier.classNames("mb-3").toAttrs()) { Text(service.title) }
                P { Text(service.description) }
                A(href = "/contact", attrs = Modifier.classNames("btn", "btn-link", "p-0").toAttrs()) {
                    Text(Strings.servicesCarouselGetAQuote[language].orEmpty())
                    I(Modifier.classNames("fa", "fa-arrow-right", "ms-2").toAttrs()) {}
                }
            }
        }
    }
}
